import React from 'react';
import classNames from 'classnames';
import { ITableColumn } from 'src/components/Table/types';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import Checkbox from 'src/components/Checkbox';
import { ISuite } from 'src/components/SharedTypes';

interface CreateKeyPickupSuiteTableColumnsParams {
  allEligibleSelected: boolean;
  onSelectAll: () => void;
  onSuiteSelect: (suiteId: string) => void;
}

export interface EnrichedSuite extends ISuite {
  isAvailableForKeyPickup: boolean;
  isSelected: boolean;
  fullName: string;
}

const enrichSuitesForKeyPickup = (
  suites: ISuite[],
  selectedSuiteIds: string[]
): EnrichedSuite[] => {
  return suites.map((suite: ISuite) => {
    const isAvailableForKeyPickup: boolean = suite.isKeyHandOff === false;
    const fullName: string =
      [suite.homeOwners?.firstname, suite.homeOwners?.lastname]
        .filter(Boolean)
        .join(' ') || 'N/A';
    const isSelected: boolean = selectedSuiteIds.includes(suite.id);

    return {
      ...suite,
      isAvailableForKeyPickup,
      fullName,
      isSelected,
    };
  });
};

const createKeyPickupSuiteTableColumns = ({
  allEligibleSelected,
  onSelectAll,
  onSuiteSelect,
}: CreateKeyPickupSuiteTableColumnsParams): ITableColumn[] => {
  return [
    {
      accessor: 'select',
      label: (
        <Checkbox
          readOnly
          value={allEligibleSelected}
          onChange={onSelectAll}
          title={
            allEligibleSelected
              ? 'Deselect all available suites'
              : 'Select all available suites'
          }
        />
      ),
      sortable: false,
      center: true,
      cell: ({ isAvailableForKeyPickup, isSelected, id }: EnrichedSuite) => {
        return (
          <Checkbox
            value={isSelected && isAvailableForKeyPickup}
            disabled={!isAvailableForKeyPickup}
            onChange={() => onSuiteSelect(id)}
            readOnly
          />
        );
      },
      style: { width: '50px' },
    },
    {
      accessor: 'number',
      label: 'Suite',
      sortable: true,
      cell: ({ isAvailableForKeyPickup, number }: EnrichedSuite) => {
        return (
          <span
            className={classNames('suite-number', {
              'suite-number--disabled': !isAvailableForKeyPickup,
            })}
          >
            {number}
          </span>
        );
      },
      style: { width: '80px' },
    },
    {
      accessor: 'homeOwners',
      label: 'Homeowner',
      sortable: true,
      cell: ({ isAvailableForKeyPickup, fullName }: EnrichedSuite) => {
        return (
          <ExpandableText
            text={fullName}
            characterLimit={25}
            className={classNames('homeowner-name', {
              'homeowner-name--disabled': !isAvailableForKeyPickup,
            })}
          />
        );
      },
      style: { width: '120px' },
    },
    {
      accessor: 'status',
      label: 'Status',
      sortable: true,
      cell: ({ isAvailableForKeyPickup }: EnrichedSuite) => {
        if (!isAvailableForKeyPickup) {
          return (
            <CategoryPill
              text="Key pickup completed"
              color="#f3f4f6"
              textColor="#6b7280"
              size="small"
              enabled={false}
              centerText={true}
            />
          );
        }

        return (
          <CategoryPill
            text="Available for key pickup"
            color="#d1fae5"
            textColor="#059669"
            size="small"
            enabled={true}
            centerText={true}
          />
        );
      },
      style: { width: '150px' },
    },
  ];
};

export { createKeyPickupSuiteTableColumns, enrichSuitesForKeyPickup };
